import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  LOCALE_ID,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import { UTCDateRange } from '@epicuro-next/ataraxia/models';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { selectCurrentDateFormat } from '@epicuro-next/platform/i18n';
import {
  makeIntervalFromDates,
  MAX_DATE,
  MIN_DATE,
  Placement,
  utcDateTime,
} from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Interval } from 'luxon';
import { shareReplay, take } from 'rxjs/operators';

import { formatDateRangeSeparator } from './utils';

export const DEFAULT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputDateRangeComponent),
  multi: true,
};

@DynamicComponentRegister('dynamic-elements-input-date-range', true)
@Component({
  selector: 'atx-input-date-range',
  templateUrl: './input-date-range.component.html',
  styleUrls: ['./input-date-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DEFAULT_VALUE_ACCESSOR],
  standalone: false,
})
export class InputDateRangeComponent implements ControlValueAccessor {
  @Input()
  public minDate: Date | string = new Date();

  @Input()
  public maxDate: Date | string | null = null;

  @Input()
  public range: UTCDateRange | null = null;

  @Input()
  public rangeBoundary = 0;

  @Input()
  public displayMonths = 1;

  @Input()
  public allowClear = false;

  @Input()
  public allowWriteValueReset = false;

  @Input()
  public placeholder = $localize`:InputDateRange|Select date range placeholder@@inputDateRangePLaceholder:Select date range`;

  @Input() public set preselected(date: UTCDateRange) {
    if (date) {
      this.rangeSelected(date);
    }
  }

  @Output() public selectionChanged = new EventEmitter<string>();

  private dateFormat$ = this.store.pipe(
    select(selectCurrentDateFormat),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public inputControl = new UntypedFormControl();

  public placement = Placement;

  private datePipe = new DatePipe(this.locale);
  public disabled = false;

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private store: Store,
  ) {}

  public get validRanges(): Interval[] {
    if (this.minDate || this.maxDate) {
      return [
        makeIntervalFromDates(
          utcDateTime(this.minDate ?? MIN_DATE),
          utcDateTime(this.maxDate ?? MAX_DATE),
        ),
      ];
    }

    return [];
  }

  public rangeSelected(value: UTCDateRange) {
    if (!value) {
      this.clear();
      return;
    }

    const { from, to } = value;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    this.setFormControlValue(fromDate, toDate);
    this.onChange([fromDate, toDate]);
    this.onTouch([fromDate, toDate]);
  }

  /**
   * Function to trigger onChange
   */
  public onChange: (arg: Date[] | undefined) => void = () => {};

  /**
   * Function to trigger onTouched
   */
  public onTouch: (arg: Date[] | undefined) => void = () => {};

  /**
   * Sets the value of the input
   * @param value Value to set
   */
  public writeValue(value: Date[] | undefined): void {
    if (!value?.length && !this.allowWriteValueReset) return;

    if (!value?.length) {
      this.inputControl.setValue(undefined);
      this.selectionChanged.emit(undefined);
    } else {
      const [from, to] = value;
      this.setFormControlValue(from, to);
    }
  }

  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onChange
   */
  public registerOnChange(fn: (arg: Date[]) => void): void {
    this.onChange = fn;
  }

  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onTouched
   */
  public registerOnTouched(fn: (arg: Date[]) => void): void {
    this.onTouch = fn;
  }

  public setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    if (disabled) {
      this.inputControl.disable();

      return;
    }

    this.inputControl.enable();
  }

  public clear() {
    this.inputControl.setValue(undefined);
    this.selectionChanged.emit(undefined);
    this.onChange(undefined);
    this.onTouch(undefined);
  }

  private setFormControlValue(from: Date, to: Date) {
    this.dateFormat$.pipe(take(1)).subscribe((dateFormat) => {
      const formattedFromDate = this.formatDateWithDotSeparator(
        from,
        dateFormat,
      );

      const formattedToDate = this.formatDateWithDotSeparator(to, dateFormat);
      const rangeString = `${formattedFromDate} - ${formattedToDate}`;

      this.inputControl.setValue(rangeString);
      this.selectionChanged.emit(rangeString);
    });
  }

  private formatDateWithDotSeparator(date: Date, dateFormat: string) {
    const selectedDate = this.datePipe.transform(date, dateFormat, 'UTC');
    return formatDateRangeSeparator(selectedDate);
  }
}
